import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { orderedFeatures, SITE_FEATURES, type SiteFeature } from '@/features/site-setup/siteSetup';
import type { SiteDetailBody } from '@/shared/api/generated/model';
import { siteFeaturesSet } from '@/shared/api/generated/sites/sites';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { Switch } from '@/shared/ui/Switch';
import { toast } from '@/shared/ui/Toast';

import styles from './SettingsPage.module.scss';

/**
 * Возможности сайта (ресерч 13): то же, что выбирают при создании. Чего у
 * сайта нет, того нет и в меню. Включение заводит недостающее на Development;
 * выключение прячет раздел, а магазин — ещё и останавливает оформление на
 * обоих окружениях. Данные не удаляются.
 */
export function FeaturesSection({ site, readOnly }: { site: SiteDetailBody; readOnly: boolean }) {
  const queryClient = useQueryClient();
  const saved = orderedFeatures(site.features ?? []);
  const [features, setFeatures] = useState<SiteFeature[]>(saved);
  const [busy, setBusy] = useState(false);

  const changed = features.join(',') !== saved.join(',');
  const shopOff = saved.includes('shop') && !features.includes('shop');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);

    siteFeaturesSet(site.id, { features })
      .then(() => {
        toast.success('Возможности сохранены');
        // Меняются меню, модели и магазин окружений — перечитать всё.
        void queryClient.invalidateQueries();
      })
      .catch((cause: unknown) => {
        toast.error('Не удалось сохранить', {
          description: cause instanceof ApiError ? cause.detail : 'Нет связи с сервером',
        });
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <form className={styles['section']} onSubmit={submit} noValidate>
      <header className={styles['section__head']}>
        <h2 className={styles['section__title']}>Возможности</h2>
        <p className={styles['section__text']}>
          Что есть на сайте. Включение заводит нужные модели на Development — на Production они
          приедут переносом. Выключение прячет раздел из меню, данные остаются.
        </p>
      </header>

      {SITE_FEATURES.map((feature) => (
        <Switch
          key={feature.key}
          checked={features.includes(feature.key)}
          disabled={readOnly || busy}
          onCheckedChange={(checked) => {
            setFeatures(
              orderedFeatures(
                checked
                  ? [...features, feature.key]
                  : features.filter((key) => key !== feature.key),
              ),
            );
          }}
        >
          <strong>{feature.title}</strong> — {feature.text}
        </Switch>
      ))}

      {shopOff && (
        <p className={styles['note']}>
          Оформление заказов на сайте остановится на обоих окружениях. Товары, заказы и остатки
          останутся — включите магазин снова, и всё вернётся.
        </p>
      )}

      {!readOnly && (
        <div>
          <Button type="submit" disabled={!changed} loading={busy}>
            Сохранить
          </Button>
        </div>
      )}
    </form>
  );
}
